/* eslint-disable */
// @ts-nocheck
import { useEffect } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save } from 'lucide-react';
import { useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { useQueryClient } from 'react-query';
import * as z from 'zod';
import Drawer from '@/components/Modals/Drawer';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import axiosClient from '@/utils/axiosClient';

const schema = z.object({
  firstName: z.string().nonempty({ message: 'First name is required' }),
  lastName: z.string().nonempty({ message: 'Last name is required' }),
  email: z.string().email({ message: 'Invalid email address' }),
  phone: z
    .string()
    .optional()
    .refine(
      (phone) => {
        if (!phone || phone.trim() === '') return true;
        const digitsOnly = phone.replace(/\D/g, '');
        return /^\d{10}$/.test(digitsOnly);
      },
      {
        message: 'Invalid phone number',
      },
    ),
  role: z.enum(['Owner', 'Member'], {
    required_error: 'Please select a role',
  }),
});

type FormData = z.infer<typeof schema>;

interface TeamMemberDrawerProps {
  accountUser?: {
    id: string;
    role: 'Owner' | 'Member';
    user: {
      first_name: string;
      last_name: string;
      email: string;
      phone: string;
    };
  };
}

// Mapper function to transform accountUser data to form fields
function mapAccountUserToFormData(accountUser: TeamMemberDrawerProps['accountUser']): FormData {
  return {
    firstName: accountUser?.user.first_name || '',
    lastName: accountUser?.user.last_name || '',
    email: accountUser?.user.email || '',
    phone: accountUser?.user.phone ? formatPhoneNumber(accountUser.user.phone) : '',
    role: accountUser?.role || 'Member',
  };
}
// Helper function to format phone number for InputMask
function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  return phone;
}

const TeamMemberDrawer = NiceModal.create((props: TeamMemberDrawerProps) => {
  const { accountUser } = props;
  const { toast } = useToast();
  const modal = useModal('team-member-drawer');
  const queryClient = useQueryClient();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // useEffect to set form values when accountUser changes
  useEffect(() => {
    if (accountUser) {
      const formData = mapAccountUserToFormData(accountUser);
      form.reset(formData);
    } else {
      form.reset({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Member',
      });
    }
  }, [accountUser, form]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload: any = {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        role: data.role,
      };

      // Only include phone if it's provided
      if (data.phone && data.phone.trim() !== '') {
        payload.phone = data.phone.replace(/\D/g, '');
      }

      if (accountUser) {
        // Update existing team member
        await axiosClient.put(`v1/team_members/${accountUser.id}`, payload);

        toast({
          title: `Updated ${data.firstName} ${data.lastName}`,
        });
      } else {
        // Create new team member
        await axiosClient.post('v1/team_members', payload);

        toast({
          title: `Added ${data.firstName} ${data.lastName}`,
        });
      }

      queryClient.invalidateQueries('v1/team_members');
      modal.hide();
    } catch (error: any) {
      console.error('Error saving team member:', error);
      if (error.response && error.response.data) {
        const { errors, message } = error.response.data;
        if (errors) {
          // Handle validation errors from the backend
          Object.entries(errors).forEach(([field, messages]: [string, any]) => {
            form.setError(field as keyof FormData, {
              type: 'server',
              message: Array.isArray(messages) ? messages.join(', ') : messages,
            });
          });
        } else {
          // Handle general errors
          toast({
            title: 'Error saving team member',
            description: message || 'An unexpected error occurred.',
            variant: 'destructive',
          });
        }
      } else {
        toast({
          title: 'Error saving team member',
          description: 'An unexpected error occurred.',
          variant: 'destructive',
        });
      }
    }
  };

  return (
    <Drawer modal={modal} title={accountUser ? 'Edit Team Member' : 'Add Team Member'}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col justify-center gap-4 p-4">
          <div className="flex flex-col gap-4">
            {/* First and Last Name Fields */}
            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="John" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Doe" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Email and Phone Fields */}
            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="example@email.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>Phone Number</FormLabel>
                    <FormControl>
                      <InputMask mask="(999) 999-9999" {...field} onChange={(e) => field.onChange(e.target.value)}>
                        {(inputProps: any) => <Input type="tel" {...inputProps} placeholder="(555) 555-5555" />}
                      </InputMask>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Role Field */}
            <div className="flex flex-row gap-4">
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Member">Member</SelectItem>
                          <SelectItem value="Owner">Owner</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="relative inline-flex items-center justify-end w-full gap-2 mt-2">
            <Button type="submit" disabled={form.formState.isSubmitting} className="w-full sm:w-fit">
              <Save />
              {form.formState.isSubmitting
                ? accountUser
                  ? 'Updating team member...'
                  : 'Inviting team member...'
                : accountUser
                  ? 'Update Team Member'
                  : 'Invite Team Member'}
            </Button>
          </div>
        </form>
      </Form>
    </Drawer>
  );
});

export default TeamMemberDrawer;
